import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { KeyRound, MailCheck, Wrench } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { invariant } from "ts-invariant";

import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { bearAuthClient } from "@/lib/bear-auth-client";
import { useSession } from "@/lib/session";

/** `DEV_MAGIC_LINK_HEADER` in `api/magic-link.ts`: only dev servers set it. */
const DEV_MAGIC_LINK_HEADER = "x-bear-dev-magic-link";

function errorMessage(error: string): string {
  return error === "INVALID_TOKEN"
    ? "That sign-in link has expired or was already used. Send yourself a new one."
    : "Google sign-in didn't finish. Please try again.";
}

/** Only a same-origin path: anything else would make this an open redirect. */
function samePath(value: unknown): string | undefined {
  return typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
    ? value
    : undefined;
}

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search): { redirect?: string; error?: string } => ({
    redirect: samePath(search.redirect),
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: SignIn,
});

function SignIn() {
  const { redirect = "/", error } = Route.useSearch();
  const { user } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(
    error ? errorMessage(error) : null,
  );
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  useEffect(() => {
    if (user) void navigate({ to: redirect });
  }, [user, navigate, redirect]);

  async function signInWithGoogle() {
    setBusy(true);
    setMessage(null);
    const result = await bearAuthClient.signIn.social({
      provider: "google",
      callbackURL: redirect,
      errorCallbackURL: "/sign-in",
    });
    if (result.error) {
      setMessage(result.error.message ?? "Google sign-in failed.");
      setBusy(false);
    }
  }

  /** The api's dev-only sign-in, so dev never needs Google. */
  async function signInAsDev() {
    setBusy(true);
    const res = await fetch("/api/auth/dev/sign-in", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email: "dev@bear.build",
        name: "Dev",
        joinBear: true,
      }),
    });
    invariant(res.ok, `Dev sign-in returned ${res.status}`);
    window.location.assign(redirect);
  }

  async function sendMagicLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const to = email.trim();
    const result = await bearAuthClient.signIn.magicLink(
      {
        email: to,
        callbackURL: redirect,
        errorCallbackURL: `/sign-in?redirect=${encodeURIComponent(redirect)}`,
      },
      {
        onResponse: ({ response }) =>
          setDevLink(response.headers.get(DEV_MAGIC_LINK_HEADER)),
      },
    );
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message ?? "Could not send the sign-in link.");
      return;
    }
    setSentTo(to);
  }

  async function signInWithPasskey() {
    setBusy(true);
    setMessage(null);
    const result = await bearAuthClient.signIn.passkey();
    if (result.error) {
      setMessage(result.error.message ?? "Passkey sign-in failed.");
    }
    setBusy(false);
  }

  return (
    <AuthShell>
      <div className="space-y-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Sign in to Bear
          </h1>
          <p className="text-sm text-muted-foreground">
            Pick up your projects where you left off.
          </p>
        </div>
        {message && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {message}
          </p>
        )}
        <div className="grid gap-2.5">
          <Button
            size="lg"
            variant="outline"
            disabled={busy}
            onClick={signInWithGoogle}
          >
            <GoogleLogo />
            Continue with Google
          </Button>
          <Button
            size="lg"
            variant="outline"
            disabled={busy}
            onClick={signInWithPasskey}
          >
            <KeyRound />
            Sign in with a passkey
          </Button>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Separator className="flex-1" />
          or use email
          <Separator className="flex-1" />
        </div>
        {sentTo ? (
          <div
            role="status"
            className="flex gap-3 rounded-lg border bg-muted/40 p-4 text-sm"
          >
            <MailCheck className="mt-0.5 size-4 shrink-0 text-brand" />
            <div className="space-y-1">
              <p className="font-medium">Check your inbox</p>
              <p className="text-muted-foreground">
                If {sentTo} has a Bear account or an invitation, a sign-in link
                is on its way.
              </p>
              {devLink && (
                <a
                  className="inline-block font-medium text-brand underline-offset-4 hover:underline"
                  href={devLink}
                >
                  Open the dev sign-in link
                </a>
              )}
            </div>
          </div>
        ) : (
          <form className="grid gap-2.5" onSubmit={sendMagicLink}>
            <label htmlFor="sign-in-email" className="text-sm font-medium">
              Work email
            </label>
            <Input
              id="sign-in-email"
              type="email"
              autoComplete="email"
              required
              placeholder="name@company.com"
              className="h-10"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button type="submit" size="lg" disabled={busy}>
              Email me a sign-in link
            </Button>
          </form>
        )}
        <p className="text-xs leading-relaxed text-muted-foreground">
          New to Bear? Continue with Google to create your account, then add a
          passkey from your profile for faster sign-in.
        </p>
        {import.meta.env.DEV && (
          <div className="rounded-lg border border-dashed p-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground"
              disabled={busy}
              onClick={signInAsDev}
            >
              <Wrench />
              Dev sign-in
            </Button>
          </div>
        )}
      </div>
    </AuthShell>
  );
}

/** Google's "G", in its brand colors. */
function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.96 10.96 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}
